'use client'

import clsx from 'clsx'
import { ChangeEvent, forwardRef, InputHTMLAttributes, useEffect, useState } from 'react'

import { Text } from '../../..'
import { useRadioGroup } from '../RadioGroup/RadioGroup'

import cl from './Radio.module.scss'

const CircleSvg = () => (
	<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
		<rect width="10" height="10" rx="5" fill="#568DFE" />
	</svg>
)

interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'checked' | 'onChange' | 'disabled'> {
	label?: string
	defaultChecked?: boolean
	checked?: boolean
	onChange?: (e: ChangeEvent<HTMLInputElement>) => void
	disabled?: boolean
	value?: string
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>((props, ref) => {
	const { label, defaultChecked, className, checked, onChange, disabled, id, value: radioValue, ...rest } = props

	const group = useRadioGroup()
	const [internalChecked, setInternalChecked] = useState(defaultChecked ?? !!checked)

	// Определяем, управляется ли компонент извне
	const isControlled = checked !== undefined

	// Если Radio внутри RadioGroup, проверяем по value
	const isCheckedByGroup = group && radioValue !== undefined ? group.value === radioValue : null
	const finalChecked = isCheckedByGroup !== null ? isCheckedByGroup : isControlled ? checked : internalChecked

	// Синхронизируем внутреннее состояние с checked prop
	useEffect(() => {
		if (isControlled && !group) {
			setInternalChecked(checked)
		}
	}, [checked, isControlled, group])

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (disabled) return

		// Если Radio в группе, передаем данные в группу
		if (group && !!radioValue) {
			group.onChange?.(radioValue)
		}

		if (!isControlled && !group) {
			setInternalChecked(e.target.checked)
		}

		onChange?.(e)
	}

	const rootClassses = clsx(cl.root, className, {
		[cl.root__disabled]: disabled
	})

	return (
		<label className={rootClassses} htmlFor={id}>
			<span className={cl.radio}>
				<input
					aria-checked={finalChecked}
					ref={ref}
					type="radio"
					className={cl.radio__input}
					checked={finalChecked}
					onChange={handleChange}
					disabled={disabled}
					id={id}
					value={radioValue}
					name={group?.name}
					{...rest}
				/>
				<span className={cl.radio__inner}>
					<CircleSvg />
				</span>
			</span>
			{label && <Text as="span">{label}</Text>}
		</label>
	)
})

Radio.displayName = 'Radio'
